import {
  chebyshevToRect,
  type CollisionGrid,
  type Direction,
  findPath,
  type TileRect,
  type TilePos,
} from '@veriverse/game-logic';

/** Clicks further than this many tiles away are ignored (keeps A* cheap on the client). */
const MAX_CLICK_DISTANCE = 60;

/**
 * Click-to-move (spec §14): the tiles to walk to reach a clicked tile. A click on something
 * solid (a desk, the coffee machine) walks to the nearest free tile next to it. `object` is the
 * footprint of an interactive object under the click, if any. `undefined` when there is no way.
 */
export function clickPath(
  grid: CollisionGrid,
  from: TilePos,
  clicked: TilePos,
  object?: TileRect,
): TilePos[] | undefined {
  if (!grid.isInside(clicked)) return undefined;
  if (Math.abs(clicked.x - from.x) + Math.abs(clicked.y - from.y) > MAX_CLICK_DISTANCE)
    return undefined;
  if (grid.isWalkable(clicked) && !object) return findPath(grid, from, clicked) ?? undefined;

  // Already standing next to it: nothing to walk.
  const target = object ?? { x: clicked.x, y: clicked.y, w: 1, h: 1 };
  if (chebyshevToRect(from, target) === 1) return [];

  let best: TilePos[] | undefined;
  for (const tile of ring(target)) {
    if (!grid.isWalkable(tile)) continue;
    const path = findPath(grid, from, tile);
    if (path && (!best || path.length < best.length)) best = path;
  }
  return best;
}

/** The tiles directly around a rectangle (diagonal corners too). */
function ring(rect: TileRect): TilePos[] {
  const tiles: TilePos[] = [];
  for (let y = rect.y - 1; y <= rect.y + rect.h; y += 1)
    for (let x = rect.x - 1; x <= rect.x + rect.w; x += 1)
      if (chebyshevToRect({ x, y }, rect) === 1) tiles.push({ x, y });
  return tiles;
}

/** The single step from `from` to the neighbouring tile `to`, or `undefined` if not adjacent. */
export function directionToward(from: TilePos, to: TilePos): Direction | undefined {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (Math.abs(dx) + Math.abs(dy) !== 1) return undefined;
  if (dx === 1) return 'right';
  if (dx === -1) return 'left';
  return dy === 1 ? 'down' : 'up';
}
